import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Paper, Typography, Card, CardContent,
  CircularProgress, Alert, Button, Stack, Avatar, LinearProgress, Chip
} from '@mui/material';
import {
  People as PeopleIcon, School as SchoolIcon, Book as BookIcon,
  TrendingUp, EmojiEvents,
  PictureAsPdf, Refresh, PersonAdd, MenuBook, Receipt,
  Male, Female, Wc, Assessment, ArrowForward, Groups, Schedule as ScheduleIcon
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { exportDashboardToPDF } from '../services/exportService';
import { usePermissions } from '../hooks/usePermissions';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6'];
import { JOURS_EDT, JOURS_EDT_TA, GrilleEmploiSemaine, normaliserRegime } from '../components/EmploiSemaine';

const PIE_COLORS = ['#6366f1', '#f472b6'];

const sansAccentsEDT = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const compactEDT = (s) => sansAccentsEDT(s).replace(/[^a-z0-9]/g, '');

// un enseignant est designe soit par son email, soit par son nom : on accepte les deux,
// comme sur la page emploi du temps, sinon la liste de seances resterait vide
// En PRIORITE, on utilise le lien explicite du compte : user.enseignant_id pointe le
// matricule ENS055 du fichier Personnel, dont le numero recoit le professeur *055
// dans l emploi du temps. Le nom et l email tolere une faute de frappe du nom, alors
// que le lien numerote, lui, est fiable.
const numeroEnseignant = (s) => {
  const m = String(s || '').match(/(\d+)\s*$/);
  return m ? m[1].replace(/^0+/, '') : '';
};
const trouverProf = (profs, user = {}) => {
  const lien = String(user.enseignant_id || '');
  if (lien) {
    const parLien = profs.filter(p => String(p.enseignantId || '') === lien);
    if (parLien.length) return parLien[0];
    const numero = numeroEnseignant(lien);
    const parNumero = profs.filter(p => numero && numeroEnseignant(p.id) === numero);
    if (parNumero.length) return parNumero[0];
  }
  const email = compactEDT(user.email);
  const nomComplet = compactEDT(`${user.prenom || ''} ${user.nom || ''}`);
  const nomSeul = compactEDT(user.nom);
  return profs.filter(p => {
    if (email && compactEDT(p.email) === email) return true;
    const cible = compactEDT(p.name);
    if (!cible) return false;
    if (nomComplet && (cible === nomComplet || compactEDT(p.short) === nomComplet)) return true;
    if (nomSeul && cible.includes(nomSeul) && nomSeul.length >= 3) return true;
    return false;
  }).sort((a, b) => {
    // en cas d'homonymie, une correspondance d'email prime sur une approximation par le nom
    const rang = (p) => (email && compactEDT(p.email) === email ? 0 : 1);
    return rang(a) - rang(b);
  })[0] || null;
};

const StatCard = ({ title, value, icon, subtitle, color, gradient, onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      borderRadius: 4,
      background: gradient || `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
      border: `1px solid ${color}20`,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': onClick ? { transform: 'translateY(-4px)', boxShadow: `0 12px 24px ${color}20` } : {},
    }}
  >
    <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ color: `${color}`, fontWeight: 500, mb: 0.5, opacity: 0.8 }}>
            {title}
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#1f2937', lineHeight: 1.2 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: '#9ca3af', mt: 0.5, display: 'block' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Avatar sx={{
          bgcolor: `${color}15`, color, width: 48, height: 48, borderRadius: 2
        }}>
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { isEnseignant, user } = usePermissions();
  // La scolarite ne gere ni les inscriptions ni les paiements : leurs raccourcis
  // disparaissent de son tableau de bord (sinon le clic renverrait vers /dashboard).
  const peutVoirInscriptionsPaiements = ['ADMIN', 'DIRECTEUR', 'CONSULTANT', 'DIRECTION_FINANCIERE', 'DIRECTION_GENERALE'].includes(user?.role);
  const [stats, setStats] = useState({
    totalEtudiants: 0, totalEnseignants: 0, totalFilieres: 0,
    totalInscriptions: 0, totalPaiements: 0,
    moyenneGenerale: 0, tauxReussiteGlobal: 0
  });
  const [statsMatiere, setStatsMatiere] = useState([]);
  const [topEtudiants, setTopEtudiants] = useState([]);
  const [evolution, setEvolution] = useState([]);
  const [repartitionSexe, setRepartitionSexe] = useState([]);
  const [emploi, setEmploi] = useState({
    prof: null, seances: [], parGroupe: [], total: 0, groupes: 0, heures: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { loadData(); }, []);

  // seances de l'enseignant : cards -> lecons (via lessonId/id) -> classes
  const loadEmploiEnseignant = async () => {
    const vide = { prof: null, seances: [], parGroupe: [], total: 0, groupes: 0, heures: 0 };
    try {
      const token = localStorage.getItem('token');
      if (!token) return vide;
      const headers = { Authorization: `Bearer ${token}` };
      const opts = { headers, timeout: 8000 };
      const [leconsRes, cardsRes, classesRes, profsRes] = await Promise.all([
        axios.get(`${API_URL}/emploi/lecons`, opts).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/emploi/cards`, opts).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/emploi/classes`, opts).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/emploi/professeurs`, opts).catch(() => ({ data: [] })),
      ]);
      const lecons = leconsRes.data || [];
      const cards = cardsRes.data || [];
      const classes = classesRes.data || [];
      const prof = trouverProf(profsRes.data || [], user || {});
      if (!prof) return vide;

      const classeDe = (id) => classes.find(x => String(x.id || x._id) === String(id));
      const nomGroupe = (id) => {
        const c = classeDe(id);
        return (c && (c.name || c.nom)) || '';
      };
      const regimeDe = (card, lecon) => {
        const direct = card.regime || lecon.regime;
        if (direct) return normaliserRegime(direct);
        const ta = (lecon.classIds || []).some(id => normaliserRegime(classeDe(id)?.regime) === 'TA');
        return ta ? 'TA' : 'NORMAL';
      };

      const seances = [];
      cards.forEach(card => {
        const lecon = lecons.find(l => String(l.id) === String(card.lessonId));
        if (!lecon || String(lecon.teacherId) !== String(prof.id)) return;
        const regime = regimeDe(card, lecon);
        const jours = [];
        const days = Number(card.days) || 0;
        (regime === 'TA' ? JOURS_EDT_TA : JOURS_EDT).forEach(j => { if (days & j.bit) jours.push(j.nom); });
        if (jours.length === 0) return;
        // une carte placee sur plusieurs jours correspond a une seance par jour
        jours.forEach(jour => {
          seances.push({
            cle: `${card.lessonId}-${card.period}-${jour}-${regime}`,
            jour,
            creneau: Number(card.period) || 0,
            duree: Number(card.duree) || 1,
            regime,
            matiere: lecon.subjectName || '',
            salle: lecon.classroomName || '',
            groupes: (lecon.classIds || []).map(nomGroupe).filter(Boolean),
          });
        });
      });

      const parGroupeMap = new Map();
      seances.forEach(s => {
        (s.groupes.length ? s.groupes : ['—']).forEach(g => {
          parGroupeMap.set(g, (parGroupeMap.get(g) || 0) + 1);
        });
      });
      const parGroupe = [...parGroupeMap.entries()]
        .map(([nom, count]) => ({ nom, count }))
        .sort((a, b) => b.count - a.count || a.nom.localeCompare(b.nom));

      // une seance simple = 1h30, une seance fusionnee (duree 2) = 3h
      const heures = seances.reduce((total, s) => total + s.duree * 1.5, 0);
      return {
        prof,
        seances,
        parGroupe,
        total: seances.length,
        groupes: new Set(seances.flatMap(s => s.groupes)).size,
        heures: Math.round(heures * 10) / 10,
      };
    } catch (err) {
      return vide;
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const timeoutConfig = { timeout: 8000 };
      const [globalRes, matiereRes, topRes, evolRes, sexeRes] = await Promise.all([
        axios.get(`${API_URL}/stats/global`, timeoutConfig).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/stats/par-matiere`, timeoutConfig).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/stats/top-etudiants?limit=5`, timeoutConfig).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/stats/evolution`, timeoutConfig).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/stats/repartition-sexe`, timeoutConfig).catch(() => ({ data: [] })),
      ]);

      const totalEtudiants = globalRes.data?.totalEtudiants || 0;
      setStats({
        totalEtudiants,
        totalEnseignants: globalRes.data?.totalEnseignants || 0,
        totalFilieres: globalRes.data?.totalFilieres || 0,
        totalInscriptions: globalRes.data?.totalInscriptions || 0,
        totalPaiements: globalRes.data?.totalPaiements || 0,
        moyenneGenerale: globalRes.data?.moyenneGenerale || 0,
        tauxReussiteGlobal: globalRes.data?.tauxReussiteGlobal || 0,
      });
      setStatsMatiere(matiereRes.data || []);
      setTopEtudiants(topRes.data || []);
      setEvolution(evolRes.data || []);
      setRepartitionSexe(sexeRes.data || []);
      if (isEnseignant) {
        setEmploi(await loadEmploiEnseignant());
      }
      setError('');
    } catch (err) {
      setError('Impossible de charger les statistiques');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    exportDashboardToPDF(stats, statsMatiere, topEtudiants);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ color: '#6366f1', mb: 2 }} />
          <Typography color="text.secondary">Chargement du tableau de bord...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadData}>Réessayer</Button>
        }>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box className="dashboard-page">
      <div className="page-container">
        {/* Header */}
        <div className="page-header">
          <div className="header-title">
            <h1>Tableau de Bord</h1>
            <p>Vue d'ensemble de la scolarité SUP2I</p>
          </div>
          <div className="header-date">
            <span>📅</span>
            <span>{new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
          </div>
        </div>

        {/* Quick Actions */}
        {!isEnseignant && (
          <div className="quick-actions" style={{ marginBottom: '1.5rem' }}>
            <h3 className="quick-actions-title">Actions rapides</h3>
            <div className="quick-actions-grid">
              <button className="quick-action-btn" onClick={() => navigate('/etudiants')}>
                <PeopleIcon sx={{ color: '#6366f1' }} /> Étudiants
              </button>
              {peutVoirInscriptionsPaiements && (
                <button className="quick-action-btn" onClick={() => navigate('/inscriptions')}>
                  <MenuBook sx={{ color: '#10b981' }} /> Inscriptions
                </button>
              )}
              {peutVoirInscriptionsPaiements && (
                <button className="quick-action-btn" onClick={() => navigate('/paiements')}>
                  <Receipt sx={{ color: '#f59e0b' }} /> Paiements
                </button>
              )}
              <button className="quick-action-btn" onClick={() => navigate('/notes')}>
                <Assessment sx={{ color: '#8b5cf6' }} /> Notes
              </button>
              <button className="quick-action-btn" onClick={loadData}>
                <Refresh sx={{ color: '#06b6d4' }} /> Actualiser
              </button>
              <button className="quick-action-btn" onClick={handleExportPDF}>
                <PictureAsPdf sx={{ color: '#ef4444' }} /> Exporter PDF
              </button>
            </div>
          </div>
        )}

        {/* KPI Cards */}
        <div className="stats-grid">
          {!isEnseignant && (
            <>
              <StatCard title="Étudiants" value={stats.totalEtudiants} icon={<PeopleIcon />} color="#6366f1" subtitle="Total inscrits" />
              <StatCard title="Enseignants" value={stats.totalEnseignants} icon={<SchoolIcon />} color="#10b981" subtitle="Corps enseignant" />
              <StatCard title="Filières" value={stats.totalFilieres} icon={<BookIcon />} color="#f59e0b" subtitle="Programmes actifs" />
              <StatCard title="Inscriptions" value={stats.totalInscriptions} icon={<MenuBook />} color="#8b5cf6" subtitle="Cette année" />
              <StatCard
                title="Moyenne Générale"
                value={stats.moyenneGenerale.toFixed(1)}
                icon={<TrendingUp />}
                color="#ec4899"
                subtitle="/20"
              />
            </>
          )}
          {isEnseignant && (
            <StatCard
              title="Mes Séances"
              value={emploi.total}
              icon={<ScheduleIcon />}
              color="#6366f1"
              subtitle="Créneaux hebdomadaires"
              onClick={() => navigate('/emploi-temps')}
            />
          )}
          {isEnseignant && (
            <StatCard
              title="Groupes"
              value={emploi.groupes}
              icon={<Groups />}
              color="#06b6d4"
              subtitle="Groupes que j'enseigne"
            />
          )}
          {isEnseignant && (
              <StatCard
                title="Volume Horaire"
                value={`${emploi.heures}h`}
                icon={<ScheduleIcon />}
                color="#f59e0b"
                subtitle="Séances fusionnées = 3h"
              />
          )}
        </div>

        {/* Taux de réussite */}
        <Card sx={{ borderRadius: 4, mb: 3, border: '1px solid #10b98120' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937' }}>
                🎯 Taux de Réussite Global
              </Typography>
              <Chip label={`${stats.tauxReussiteGlobal}%`} color={stats.tauxReussiteGlobal >= 60 ? 'success' : 'warning'} />
            </Box>
            <LinearProgress
              variant="determinate"
              value={stats.tauxReussiteGlobal}
              sx={{
                height: 10, borderRadius: 5,
                bgcolor: '#e5e7eb',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 5,
                  background: stats.tauxReussiteGlobal >= 60
                    ? 'linear-gradient(90deg, #10b981, #059669)'
                    : 'linear-gradient(90deg, #f59e0b, #d97706)'
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Basé sur les notes enregistrées
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {stats.tauxReussiteGlobal}% de réussite
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Charts Row */}
        <Grid container spacing={3}>
          {/* Bar Chart - Matières */}
          <Grid item xs={12} md={isEnseignant ? 12 : 7}>
            <Paper sx={{ p: 3, borderRadius: 4, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 2 }}>
                📊 Résultats par matière
              </Typography>
              {statsMatiere.length > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={statsMatiere} margin={{ left: -10, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="nom" angle={-35} textAnchor="end" height={60} tick={{ fontSize: 11, fill: '#6b7280' }} />
                    <YAxis domain={[0, 20]} tick={{ fontSize: 12, fill: '#6b7280' }} />
                    <Tooltip
                      contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                      formatter={(val) => [`${val}/20`, 'Moyenne']}
                    />
                    <Bar dataKey="moyenne" radius={[6, 6, 0, 0]}>
                      {statsMatiere.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <Typography color="text.secondary">Aucune donnée de notes disponible</Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Right Column */}
          {!isEnseignant && (
          <Grid item xs={12} md={5}>
            <Stack spacing={3}>
              {/* Pie Chart - Sexe */}
              <Paper sx={{ p: 3, borderRadius: 4, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 2 }}>
                  👥 Répartition par sexe
                </Typography>
                {repartitionSexe.length > 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                    <ResponsiveContainer width={160} height={160}>
                      <PieChart>
                        <Pie data={repartitionSexe} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="count" nameKey="sexe">
                          {repartitionSexe.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <Box>
                      {repartitionSexe.map((item, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: PIE_COLORS[i] }} />
                          <Typography variant="body2" sx={{ color: '#4b5563' }}>
                            {item.sexe === 'M' ? 'Masculin' : item.sexe === 'F' ? 'Féminin' : item.sexe || 'Non spécifié'}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                            {item.count}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography color="text.secondary">Aucune donnée</Typography>
                  </Box>
                )}
              </Paper>

              {/* Top Étudiants */}
              <Paper sx={{ p: 3, borderRadius: 4, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 2 }}>
                  🏆 Top 5 Étudiants
                </Typography>
                {topEtudiants.length > 0 ? (
                  <Box>
                    {topEtudiants.map((etudiant, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          py: 1.2, borderBottom: index < topEtudiants.length - 1 ? '1px solid #f3f4f6' : 'none'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{
                            width: 30, height: 30, borderRadius: '50%',
                            background: index < 3 ? '#fef3c7' : '#f3f4f6',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: 700, fontSize: '0.8rem', color: index < 3 ? '#d97706' : '#6b7280'
                          }}>
                            {index + 1}
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151' }}>
                            {etudiant.prenom} {etudiant.nom}
                          </Typography>
                        </Box>
                        <Chip
                          label={`${etudiant.moyenne}/20`}
                          size="small"
                          sx={{
                            fontWeight: 600, fontSize: '0.75rem',
                            bgcolor: index === 0 ? '#10b981' : index === 1 ? '#6366f1' : index === 2 ? '#f59e0b' : '#f3f4f6',
                            color: index < 3 ? 'white' : '#6b7280',
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography color="text.secondary">Aucune note enregistrée</Typography>
                  </Box>
                )}
              </Paper>
            </Stack>
          </Grid>
          )}
        </Grid>

        {/* Bloc enseignant : seances par groupe + emploi du temps */}
        {isEnseignant && (
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 3, height: '100%', borderRadius: 4, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 0.5 }}>
                  👥 Mes séances par groupe
                </Typography>
                <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                  {emploi.total} séance{emploi.total > 1 ? 's' : ''} répartie{emploi.total > 1 ? 's' : ''} sur {emploi.groupes} groupe{emploi.groupes > 1 ? 's' : ''}
                </Typography>
                {emploi.parGroupe.length > 0 ? (
                  <Box sx={{ mt: 2 }}>
                    {emploi.parGroupe.map(g => {
                      const pct = emploi.total > 0 ? Math.round((g.count / emploi.total) * 100) : 0;
                      return (
                        <Box key={g.nom} sx={{ mb: 1.8 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151' }}>
                              {g.nom}
                            </Typography>
                            <Chip
                              label={`${g.count} séance${g.count > 1 ? 's' : ''}`}
                              size="small"
                              sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: '#eef2ff', color: '#4f46e5' }}
                            />
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            sx={{
                              height: 8, borderRadius: 99, bgcolor: '#f1f5f9',
                              '& .MuiLinearProgress-bar': { borderRadius: 99, background: '#6366f1' }
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">Aucune séance assignée</Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={7}>
              <Paper sx={{ p: 3, borderRadius: 4, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 2 }}>
                  🗓️ Mon emploi du temps
                </Typography>
                {emploi.seances.length === 0 && (
                  <GrilleEmploiSemaine seances={emploi.seances} showGroupes />
                )}
                {emploi.seances.some(s => normaliserRegime(s.regime) === 'NORMAL') && (
                  <Box sx={{ mb: emploi.seances.some(s => normaliserRegime(s.regime) === 'TA') ? 3 : 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1 }}>
                      Enseignement normal
                    </Typography>
                    <GrilleEmploiSemaine seances={emploi.seances} showGroupes regime="NORMAL" />
                  </Box>
                )}
                {emploi.seances.some(s => normaliserRegime(s.regime) === 'TA') && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1 }}>
                      Temps aménagé (TA)
                    </Typography>
                    <GrilleEmploiSemaine seances={emploi.seances} showGroupes regime="TA" />
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Evolution Chart */}
        {evolution.length > 0 && (
          <Paper sx={{ p: 3, borderRadius: 4, mt: 3, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 2 }}>
              📈 Évolution des notes
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={evolution}>
                <defs>
                  <linearGradient id="colorMoy" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="periode" tick={{ fontSize: 11, fill: '#6b7280' }} />
                <YAxis domain={[0, 20]} tick={{ fontSize: 12, fill: '#6b7280' }} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  formatter={(val) => [`${val}/20`, 'Moyenne']}
                />
                <Area type="monotone" dataKey="moyenne" stroke="#6366f1" strokeWidth={2} fill="url(#colorMoy)" />
                <Line type="monotone" dataKey="total" stroke="#10b981" strokeWidth={2} name="Nombre de notes" />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        )}

        </div>
    </Box>
  );
};

export default Dashboard;


